import { isPublicAppPath } from '@/utils/public-routes'

const POST_LOGIN_REDIRECT_STORAGE_KEY = 'cc.postLoginRedirectPath'
const LOGIN_PATH = '/api/registry/v1/login'

const buildCurrentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`

// Query and hash are irrelevant for the public-route check, so they are stripped
// before matching the stored path against the public path prefixes.
const pathnameOf = (path: string) => path.split(/[?#]/, 1)[0] ?? path

const isSafeInternalRedirectPath = (path: string | null): path is string => {
  if (!path) return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//') || path.startsWith('/\\')) return false
  if (path.startsWith(LOGIN_PATH)) return false
  return true
}

const isValidRedirectPath = (path: string | null) => {
  if (!isSafeInternalRedirectPath(path)) return false
  // Public routes (customer onboarding) intentionally have no portal session.
  // Returning to them after a portal login would show the onboarding wizard
  // instead of the dashboard, so they are never valid post-login targets.
  if (isPublicAppPath(pathnameOf(path))) return false
  return true
}

export interface RedirectToLoginOptions {
  prompt?: string
  redirect?: string
  returnTo?: string
  customer?: string
  customerNo?: string
  tenant?: string
  tenantName?: string
  allowPublicRedirect?: boolean
}

const extractCustomerAndTenantFromQueryString = (queryOrSearch: string) => {
  try {
    const qIndex = queryOrSearch.indexOf('?')
    const search = qIndex !== -1 ? queryOrSearch.slice(qIndex) : queryOrSearch
    const searchParams = new URLSearchParams(search)
    const customer = searchParams.get('customer') || searchParams.get('customerNo') || undefined
    const tenant = searchParams.get('tenant') || searchParams.get('tenantName') || undefined
    return { customer, tenant }
  } catch {
    return { customer: undefined, tenant: undefined }
  }
}

export const savePostLoginRedirectPath = (path = buildCurrentPath()) => {
  if (!isValidRedirectPath(path)) return
  sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path)
}

export const consumePostLoginRedirectPath = () => {
  const storedPath = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)

  if (!isValidRedirectPath(storedPath)) return null
  return storedPath
}

export const redirectToLogin = (options?: RedirectToLoginOptions | string) => {
  let prompt: string | undefined
  let targetPath: string | undefined
  let customer: string | undefined
  let tenant: string | undefined
  let allowPublicRedirect = false

  if (typeof options === 'string') {
    targetPath = options
  } else if (options) {
    prompt = options.prompt
    targetPath = options.redirect ?? options.returnTo
    customer = options.customer ?? options.customerNo
    tenant = options.tenant ?? options.tenantName
    allowPublicRedirect = options.allowPublicRedirect === true
  }

  const redirectPath = targetPath ?? buildCurrentPath()
  savePostLoginRedirectPath(redirectPath)

  if (!customer || !tenant) {
    const fromTarget = extractCustomerAndTenantFromQueryString(redirectPath)
    if (!customer) customer = fromTarget.customer
    if (!tenant) tenant = fromTarget.tenant
  }

  if (typeof window !== 'undefined' && window.location?.search && (!customer || !tenant)) {
    const fromWindow = extractCustomerAndTenantFromQueryString(window.location.search)
    if (!customer) customer = fromWindow.customer
    if (!tenant) tenant = fromWindow.tenant
  }

  const params = new URLSearchParams()
  if (prompt) {
    params.set('prompt', prompt)
  }
  if (customer) {
    params.set('customer', customer)
  }
  if (tenant) {
    params.set('tenant', tenant)
  }
  if (
    isValidRedirectPath(redirectPath) ||
    (allowPublicRedirect && isSafeInternalRedirectPath(redirectPath))
  ) {
    params.set('redirect', redirectPath)
  }

  const query = params.toString()
  const loginUrl = query ? `${LOGIN_PATH}?${query}` : LOGIN_PATH
  window.location.assign(loginUrl)
}
